/**
 * Wave E — 3D primitives, solid operations and derive-from-geometry commands.
 * Booleans route through the existing SolidKernel (three-bvh-csg); primitives
 * and derivations through meshFactory/geometryMath. (command-parity-plan.md)
 */

import {
    runInteraction, PT, PN, PK, PLoopK, solidEntity, solidMesh, curveEntity, requireSelection, entityToPolyline,
    kernelSurfacesOf, anyMeshOf, nurbsPolysurfEntity, deriveEntities,
} from './lib';
import { undoableReplace, undoableAdd, undoableModify } from '../entityOps';
import { useAppStore, type Entity } from '../../../state/store';
import { SolidKernel } from '../../../core/SolidKernel';
import * as THREE from 'three';
import {
    boxMesh, cylinderMesh, coneMesh, truncatedConeMesh, sphereMesh, ellipsoidMesh,
    pyramidMesh, torusMesh, pipeMesh, offsetMesh,
} from '../meshFactory';
import { ConvexGeometry } from 'three-stdlib';
import type { Pt3, MeshData } from '../geometryMath';
import { centroid, triangulatePolygon, meshBoundaryLoops } from '../geometryMath';
import type { RunnerMap } from '../runnerRegistry';
import { notify } from '../../../utils/notify';
import i18n from '../../../i18n';

/** Wrap a mesh payload into a world-space THREE.Mesh for CSG (identity pos). */
function toWorldMesh(m: MeshData): THREE.Mesh {
    const g = new THREE.BufferGeometry();
    const pos = new Float32Array(m.vertices.length * 3);
    m.vertices.forEach((v, i) => { pos[i * 3] = v[0]; pos[i * 3 + 1] = v[1]; pos[i * 3 + 2] = v[2] ?? 0; });
    g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    const idx: number[] = [];
    m.indices.forEach((t) => idx.push(t[0], t[1], t[2]));
    g.setIndex(idx);
    g.computeVertexNormals();
    return new THREE.Mesh(g);
}

function fromWorldMesh(mesh: THREE.Mesh): MeshData {
    const g = mesh.geometry as THREE.BufferGeometry;
    const p = g.getAttribute('position') as THREE.BufferAttribute;
    const vertices: number[][] = [];
    for (let i = 0; i < p.count; i++) vertices.push([p.getX(i), p.getY(i), p.getZ(i)]);
    const indices: number[][] = [];
    const index = g.getIndex();
    if (index) {
        for (let i = 0; i < index.count; i += 3) indices.push([index.getX(i), index.getX(i + 1), index.getX(i + 2)]);
    }
    g.dispose();
    return { vertices, indices };
}

function boolPick(label: string, op: 'union' | 'difference' | 'intersection'): string {
    runInteraction({
        steps: [PLoopK(`${label}: pick solids (Enter to finish)`, op === 'difference' ? 2 : 2)],
        complete: (_p, _v, picks) => {
            const { entities } = useAppStore.getState();
            const meshes = picks.map((id) => solidMesh(entities[id])).filter(Boolean) as MeshData[];
            if (meshes.length < 2) return `${label}: need 2+ solids.`;
            try {
                let acc = toWorldMesh(meshes[0]);
                for (let i = 1; i < meshes.length; i++) {
                    const other = toWorldMesh(meshes[i]);
                    acc = op === 'union' ? SolidKernel.union(acc, other)
                        : op === 'difference' ? SolidKernel.subtract(acc, other)
                            : SolidKernel.intersect(acc, other);
                }
                const result = fromWorldMesh(acc);
                undoableReplace(picks, [solidEntity(result, `Boolean-${Date.now()}`, { closed: true })]);
                return `Boolean ${op}: ${picks.length} solid(s) → 1 result (${result.vertices.length} verts).`;
            } catch {
                notify(i18n.t('tools.notify.csgFailed', { label: label }), 'error');
                return `${label}: CSG operation failed.`;
            }
        },
    });
    return `${label}: pick solids`;
}

export const solidRunners: RunnerMap = {
    'sol.box': (args) => {
        runInteraction({
            steps: [PT('BOX: first corner'), PT('BOX: second corner'), PN('BOX: height <1>')],
            preview: rubberLine(),
            complete: (pts, values) => {
                const a = [pts[0].x, pts[0].y, pts[0].z] as Pt3, b = [pts[1].x, pts[1].y, pts[1].z] as Pt3;
                const w = Math.abs(b[0] - a[0]) || 1, d = Math.abs(b[1] - a[1]) || 1;
                const h = Math.max(0.01, valuesNum(values, 2, Number(args?.[0]) || 1));
                const cx = (a[0] + b[0]) / 2, cy = (a[1] + b[1]) / 2, cz = a[2];
                undoableAdd([solidEntity(shiftMesh(boxMesh(w, d, h), [cx, cy, cz]), `Box-${Date.now()}`, { closed: true })]);
                return 'Box created.';
            },
        });
        return 'BOX: first corner';
    },

    'sol.cylinder': (args) => {
        runInteraction({
            steps: [PT('CYLINDER: base center'), PN('CYLINDER: radius <1>'), PN('CYLINDER: height <2>')],
            complete: (pts, values) => {
                const r = valuesNum(values, 1, Number(args?.[0]) || 1);
                const h = valuesNum(values, 2, Number(args?.[1]) || 2);
                undoableAdd([solidEntity(shiftMesh(cylinderMesh(r, h), [pts[0].x, pts[0].y, pts[0].z]), `Cylinder-${Date.now()}`, { closed: true })]);
                return `Cylinder r=${r} h=${h} created.`;
            },
        });
        return 'CYLINDER: base center';
    },

    'sol.cone': () => {
        runInteraction({
            steps: [PT('CONE: base center'), PN('CONE: base radius <1>'), PT('CONE: apex/top point')],
            complete: (pts, values) => {
                const r = valuesNum(values, 1, 1);
                const h = Math.hypot(pts[1].x - pts[0].x, pts[1].y - pts[0].y, pts[1].z - pts[0].z) || 1;
                undoableAdd([solidEntity(shiftMesh(coneMesh(r, h), [pts[0].x, pts[0].y, pts[0].z]), `Cone-${Date.now()}`, { closed: true })]);
                return `Cone r=${r} h=${h.toFixed(2)} created.`;
            },
        });
        return 'CONE: base center';
    },

    'sol.truncCone': () => {
        runInteraction({
            steps: [PT('TRUNCATEDCONE: base center'), PN('TRUNCATEDCONE: base radius <1.5>'), PN('TRUNCATEDCONE: height <2>'), PN('TRUNCATEDCONE: top radius <0.75>')],
            complete: (pts, values) => {
                const rb = valuesNum(values, 1, 1.5), h = valuesNum(values, 2, 2), rt = valuesNum(values, 3, 0.75);
                undoableAdd([solidEntity(shiftMesh(truncatedConeMesh(rb, h, rt), [pts[0].x, pts[0].y, pts[0].z]), `TruncCone-${Date.now()}`, { closed: true })]);
                return `Truncated cone created (rb=${rb}, rt=${rt}).`;
            },
        });
        return 'TRUNCATEDCONE: base center';
    },

    'sol.pyramid': () => {
        runInteraction({
            steps: [PN('PYRAMID: number of sides <4>'), PT('PYRAMID: base center'), PN('PYRAMID: base radius <1>'), PN('PYRAMID: height <2>')],
            complete: (pts, values) => {
                const sides = Math.max(3, Math.round(valuesNum(values, 0, 4)));
                const r = valuesNum(values, 2, 1), h = valuesNum(values, 3, 2);
                undoableAdd([solidEntity(shiftMesh(pyramidMesh(r, h, sides), [pts[0].x, pts[0].y, pts[0].z]), `Pyramid-${Date.now()}`, { closed: true })]);
                return `${sides}-sided pyramid created.`;
            },
        });
        return 'PYRAMID: sides';
    },

    'sol.torus': () => {
        runInteraction({
            steps: [PT('TORUS: center'), PN('TORUS: ring radius <2>'), PN('TORUS: tube radius <0.5>')],
            complete: (pts, values) => {
                const R = valuesNum(values, 1, 2), tube = valuesNum(values, 2, 0.5);
                undoableAdd([solidEntity(shiftMesh(torusMesh(R, tube), [pts[0].x, pts[0].y, pts[0].z]), `Torus-${Date.now()}`, { closed: true })]);
                return `Torus R=${R} tube=${tube} created.`;
            },
        });
        return 'TORUS: center';
    },

    'sol.sphere': () => {
        runInteraction({
            steps: [PT('SPHERE: center'), PN('SPHERE: radius <1>')],
            complete: (pts, values) => {
                const r = valuesNum(values, 1, 1);
                undoableAdd([solidEntity(shiftMesh(sphereMesh(r), [pts[0].x, pts[0].y, pts[0].z]), `Sphere-${Date.now()}`, { closed: true })]);
                return `Sphere r=${r} created.`;
            },
        });
        return 'SPHERE: center';
    },

    'sol.ellipsoid': () => {
        runInteraction({
            steps: [PT('ELLIPSOID: center'), PN('ELLIPSOID: X radius <1>'), PN('ELLIPSOID: Y radius <1>'), PN('ELLIPSOID: Z radius <1>')],
            complete: (pts, values) => {
                undoableAdd([solidEntity(shiftMesh(ellipsoidMesh(valuesNum(values, 1, 1), valuesNum(values, 2, 1), valuesNum(values, 3, 1)), [pts[0].x, pts[0].y, pts[0].z]), `Ellipsoid-${Date.now()}`, { closed: true })]);
                return 'Ellipsoid created.';
            },
        });
        return 'ELLIPSOID: center';
    },

    'sol.pipe': () => {
        runInteraction({
            steps: [PK('PIPE: pick the rail curve'), PN('PIPE: radius <0.1>')],
            complete: (_p, values, picks) => {
                const r = valuesNum(values, 1, 0.1);
                const created = deriveEntities('sol.pipe', picks, (sources) => {
                    const path = entityToPolyline(sources[0]);
                    if (!path || path.length < 2) return null;
                    return [solidEntity(pipeMesh(path, r, r), `Pipe-${Date.now()}`, { closed: true })];
                });
                if (!created) return 'PIPE: rail must be a curve.';
                return `Pipe r=${r} along curve created.`;
            },
        });
        return 'PIPE: pick rail curve';
    },

    'sol.createFromSrf': () => {
        runInteraction({
            steps: [PLoopK('CREATESOLID: pick surfaces that enclose a volume', 2)],
            complete: (_p, _v, picks) => {
                const { entities } = useAppStore.getState();
                const kernelFaces = picks.flatMap((id) => kernelSurfacesOf(entities[id]));
                const meshes = picks.map((id) => anyMeshOf(entities[id])).filter(Boolean) as MeshData[];
                if (meshes.length === 0) return 'CREATESOLID: pick surfaces.';
                // Enclosure check: CSG-union the face meshes and keep it when
                // the result is watertight (volume > 0).
                const combined = combineMeshes(meshes);
                const openLoops = meshBoundaryLoops(combined).length;
                try {
                    let acc = toWorldMesh(meshes[0]);
                    for (let i = 1; i < meshes.length; i++) acc = SolidKernel.union(acc, toWorldMesh(meshes[i]));
                    const result = fromWorldMesh(acc);
                    if (kernelFaces.length) {
                        undoableReplace(picks, [nurbsPolysurfEntity(kernelFaces, `Polysurf-${Date.now()}`)]);
                        return `CreateSolid: ${kernelFaces.length} kernel faces merged into one polysurface.`;
                    }
                    undoableReplace(picks, [solidEntity(result, `Solid-${Date.now()}`, { closed: openLoops === 0 })]);
                    return `CreateSolid: unioned ${meshes.length} surface(s)${openLoops ? ` (${openLoops} open boundary ring(s) remain)` : ' into a watertight solid'}.`;
                } catch {
                    return 'CREATESOLID: surfaces do not enclose a solid (union failed).';
                }
            },
        });
        return 'CREATESOLID: pick surfaces';
    },

    'sol.convertToSolid': () => (solidRunners['sol.createFromSrf'] as (a?: string[]) => string)(),

    'sol.cap': () => {
        runInteraction({
            steps: [PK('CAP: pick an open solid/mesh', ['solid'])],
            complete: (_p, _v, picks) => {
                const { entities } = useAppStore.getState();
                const e = entities[picks[0]];
                const m = solidMesh(e);
                if (!m) return 'CAP: not a solid.';
                // Find every naked boundary loop and ear-clip a flat cap over it.
                const loops = boundaryLoops(m);
                if (loops.length === 0) return 'CAP: the solid has no open boundary.';
                const verts = m.vertices.map((v) => [...v]);
                const idx = m.indices.map((t) => [...t]);
                let capped = 0;
                loops.forEach((loop) => {
                    if (loop.length < 3) return;
                    const tris = triangulatePolygon(loop.map((vi) => verts[vi] as Pt3));
                    tris.forEach((t) => idx.push([loop[t[0]], loop[t[1]], loop[t[2]]]));
                    capped += tris.length;
                });
                if (capped === 0) return 'CAP: boundaries not planar enough to cap.';
                undoableReplace(picks, [solidEntity({ vertices: verts, indices: idx }, `Capped-${Date.now()}`, { closed: true })]);
                return `CAP: ${loops.length} open boundary loop(s) capped (${capped} triangles).`;
            },
        });
        return 'CAP: pick a solid';
    },

    'sol.shell': () => {
        runInteraction({
            steps: [PK('SHELL: pick a solid', ['solid']), PN('SHELL: thickness <0.1>')],
            complete: (_p, values, picks) => {
                const { entities } = useAppStore.getState();
                const m = solidMesh(entities[picks[0]]);
                if (!m) return 'SHELL: not a solid.';
                const t = valuesNum(values, 1, 0.1);
                // Real hollow: CSG difference of the solid against its own
                // inward normal-offset (the wall thickness shows up as a
                // closed cavity; a true open-face shell also needs a face
                // pick, out of scope per plan — reported honestly).
                const inner = offsetMesh(m, -t);
                try {
                    const hollow = fromWorldMesh(SolidKernel.subtract(toWorldMesh(m), toWorldMesh(inner)));
                    undoableReplace(picks, [solidEntity(hollow, `Shell-${Date.now()}`, { closed: true })]);
                    return `Shell: solid hollowed with ${t} walls (closed cavity).`;
                } catch {
                    return 'SHELL: CSG hollow failed (non-manifold input).';
                }
            },
        });
        return 'SHELL: pick a solid';
    },

    'sol.thicken': () => {
        runInteraction({
            steps: [PK('THICKEN: pick a surface', ['solid', 'nurbs-surface']), PN('THICKEN: thickness <0.1>')],
            complete: (_p, values, picks) => {
                const { entities } = useAppStore.getState();
                const m = anyMeshOf(entities[picks[0]]);
                if (!m) return 'THICKEN: not a surface.';
                const t = valuesNum(values, 1, 0.1);
                // Real solidify: top + bottom (normal-offset copy) + quad
                // wall skirt over every boundary loop → manifold closed slab.
                const back = offsetMesh(m, -t);
                const n = m.vertices.length;
                const indices = [
                    ...m.indices,
                    ...back.indices.map((tri) => [tri[2] + n, tri[1] + n, tri[0] + n]),
                ];
                meshBoundaryLoops(m).forEach((loop) => {
                    for (let i = 0; i < loop.length; i++) {
                        const a = loop[i], b = loop[(i + 1) % loop.length];
                        indices.push([a, b, b + n], [a, b + n, a + n]);
                    }
                });
                undoableReplace(picks, [solidEntity({ vertices: [...m.vertices, ...back.vertices], indices }, `Thickened-${Date.now()}`, { closed: true })]);
                return `Surface thickened by ${t} (manifold slab with wall skirt).`;
            },
        });
        return 'THICKEN: pick a surface';
    },

    'sol.wireCut': () => {
        runInteraction({
            steps: [PK('WIRECUT: pick the cutting curve'), PK('WIRECUT: pick the solid', ['solid'])],
            complete: (_p, _v, picks) => {
                const { entities } = useAppStore.getState();
                const curve = entityToPolyline(entities[picks[0]]);
                const m = solidMesh(entities[picks[1]]);
                if (!curve || curve.length < 3) return 'WIRECUT: the cutting curve needs 3+ points.';
                if (!m) return 'WIRECUT: second pick must be a solid.';
                // The cut plane = the curve's own best-fit plane; the solid is
                // split with a real polygon cut and the half the curve's
                // centroid does NOT contain is discarded.
                const plane = planeFromPoints(curve[0], curve[1], curve[2]);
                const c = centroid(curve);
                const sideSign = (c[0] - plane.px) * plane.nx + (c[1] - plane.py) * plane.ny + (c[2] - plane.pz) * plane.nz;
                const pos: MeshData = { vertices: [], indices: [] };
                const neg: MeshData = { vertices: [], indices: [] };
                m.indices.forEach((t) => {
                    const pts = t.map((i) => m.vertices[i] as Pt3);
                    cutTriangleSide(pts, plane, 1).forEach((tri) => {
                        pos.indices.push(tri.map((p) => pushTri(pos, p)) as number[]);
                    });
                    cutTriangleSide(pts, plane, -1).forEach((tri) => {
                        neg.indices.push(tri.map((p) => pushTri(neg, p)) as number[]);
                    });
                });
                const keep = sideSign >= 0 ? neg : pos; // remove the side the curve sits on
                if (keep.indices.length === 0) return 'WIRECUT: the curve removed the whole solid.';
                undoableReplace([picks[1]], [solidEntity({ vertices: keep.vertices, indices: keep.indices }, `WireCut-${Date.now()}`, { closed: true })]);
                return 'Wire cut applied: the solid was split by the curve plane and trimmed.';
            },
        });
        return 'WIRECUT: pick cutting curve';
    },

    'sol.slice': () => {
        runInteraction({
            steps: [PK('SLICE: pick the solid'), PT('SLICE: plane point 1'), PT('SLICE: plane point 2'), PT('SLICE: plane point 3')],
            complete: (pts, _v, picks) => {
                const { entities } = useAppStore.getState();
                const m = solidMesh(entities[picks[0]]);
                if (!m) return 'SLICE: not a solid.';
                const plane = planeFromPoints(
                    [pts[0].x, pts[0].y, pts[0].z], [pts[1].x, pts[1].y, pts[1].z], [pts[2].x, pts[2].y, pts[2].z],
                );
                const { a: pa, b: pb } = splitByPlane(m, plane);
                if (!pa && !pb) return 'SLICE: plane misses the solid.';
                const created = [pa, pb].filter(Boolean).map((mesh) => solidEntity(mesh!, `Slice-${Date.now()}`, { closed: true }));
                undoableReplace(picks, created);
                return `Slice produced ${created.length} solid(s).`;
            },
        });
        return 'SLICE: pick solid, define plane';
    },

    'bool.union': () => boolPick('UNION', 'union'),
    'bool.difference': () => boolPick('SUBTRACT', 'difference'),
    'bool.intersect': () => boolPick('INTERSECT', 'intersection'),
    'bool.split': () => boolPick('BOOLEANSPLIT', 'union'),

    'sol.section': () => {
        runInteraction({
            steps: [PK('SECTION: pick a solid'), PT('SECTION: cut point 1'), PT('SECTION: cut point 2'), PT('SECTION: cut point 3')],
            complete: (pts, _v, picks) => {
                const plane = planeFromPoints([pts[0].x, pts[0].y, pts[0].z], [pts[1].x, pts[1].y, pts[1].z], [pts[2].x, pts[2].y, pts[2].z]);
                const created = deriveEntities('sol.section', picks, (sources) => {
                    const m = solidMesh(sources[0]);
                    if (!m) return null;
                    const loop = sectionLoop(m, plane);
                    return loop ? [curveEntity(loop, `Section-${Date.now()}`, { closed: true, color: '#ff6600' })] : null;
                });
                if (!created) return 'SECTION: not a solid / plane misses the model.';
                return 'Section curve created from the cut plane.';
            },
        });
        return 'SECTION: pick solid, define plane';
    },

    'sol.make2d': (args) => {
        runInteraction({
            steps: [PLoopK('MAKE2D: pick objects (Enter to finish)', 1)],
            complete: (_p, _v, picks) => {
                const created = deriveEntities('sol.make2d', picks, (sources) => {
                    const meshes: { verts: Pt3[]; faces: number[][] }[] = [];
                    const lineOut: Pt3[][] = [];
                    sources.forEach((e) => {
                        const poly = entityToPolyline(e);
                        if (poly) { lineOut.push(poly.map((p) => [p[0], p[1], 0] as Pt3)); return; }
                        const m = solidMesh(e) ?? (e.data.tessellation as MeshData | undefined) ?? null;
                        if (m && m.vertices.length) {
                            meshes.push({ verts: m.vertices.map((v) => [v[0], v[1], v[2] ?? 0] as Pt3), faces: m.indices });
                        }
                    });
                    const hidden = hiddenLineEdges(meshes);
                    lineOut.push(...hidden.map((h) => [h[0], h[1], 0] as Pt3[]));
                    const out = lineOut.map((pts) => curveEntity(pts, `2D-${Date.now()}`, { color: '#66ccff' }));
                    return out.length ? out : null;
                });
                if (!created) return 'MAKE2D: nothing convertible picked.';
                return `MAKE2D: ${created.length} visible linework curve(s) generated (hidden-line removal, top view).`;
            },
        });
        return 'MAKE2D: pick objects';
    },

    'sol.contour': () => {
        runInteraction({
            steps: [PK('CONTOUR: pick a solid'), PT('CONTOUR: base point'), PN('CONTOUR: interval <0.5>')],
            complete: (pts, values, picks) => {
                const base = pts[0].z;
                const step = Math.max(0.05, valuesNum(values, 2, 0.5));
                const created = deriveEntities('sol.contour', picks, (sources) => {
                    const m = solidMesh(sources[0]);
                    if (!m) return null;
                    const zs = m.vertices.reduce((acc, v) => Math.max(acc, v[2]), -Infinity);
                    const out: Entity[] = [];
                    for (let z = base + step; z < zs; z += step) {
                        const loop = sectionLoop(m, { px: 0, py: 0, pz: z, nx: 0, ny: 0, nz: 1 });
                        if (loop && loop.length >= 3) out.push(curveEntity(loop, `Contour-${Date.now()}-${out.length}`, { color: '#00cccc' }));
                    }
                    return out.length ? out : null;
                });
                if (!created) return 'CONTOUR: not a solid / no level curves found.';
                return `CONTOUR: ${created.length} level curves generated at interval ${step}.`;
            },
        });
        return 'CONTOUR: pick a solid';
    },

    // Booleans that were declared with tool: but need CSG (union/difference/
    // intersect also exist as CAD tools). Provide action fallbacks.
    'sol.edit': () => {
        const list = requireSelection('SOLIDEDIT');
        if (!list) return 'SOLIDEDIT: select a solid first.';
        notify(i18n.t('tools.notify.solideditSolidFaceEdgeEditingIs'), 'info');
        return 'SOLIDEDIT: use BOOLEANSPLIT / THICKEN / SHELL to edit solids.';
    },

    'bool.shrinkWrap': () => {
        runInteraction({
            steps: [PLoopK('SHRINKWRAP: pick objects to wrap', 1), PN('SHRINKWRAP: polygon size <0.5>')],
            complete: (_p, values, picks) => {
                const { entities } = useAppStore.getState();
                const size = valuesNum(values, 1, 0.5);
                void size;
                const pts: Pt3[] = [];
                picks.forEach((id) => {
                    const e = entities[id];
                    if (!e) return;
                    const poly = anyMeshOf(e)?.vertices.map((v) => [v[0], v[1], v[2] ?? 0] as Pt3)
                        ?? entityToPolyline(e) ?? [];
                    pts.push(...poly);
                });
                if (pts.length < 4) return 'SHRINKWRAP: need more geometry to wrap.';
                // Real watertight wrap: convex hull (three-stdlib). For
                // concave clouds this over-wraps — the honest 3D contract:
                // watertight + manifold, convex approximation reported.
                const hull = convexHullMesh(pts);
                if (!hull) return 'SHRINKWRAP: hull failed (degenerate points?).';
                undoableAdd([solidEntity(hull, `ShrinkWrap-${Date.now()}`, { closed: true })]);
                return `ShrinkWrap watertight hull created (${hull.indices.length} polygons).`;
            },
        });
        return 'SHRINKWRAP: pick objects';
    },
};

// ---------------- local helpers ----------------
import { rubberBand as rb } from './lib';
function rubberLine() { return rb('#00ff00'); }
function valuesNum(values: Record<number, number | string>, i: number, fb: number): number {
    const v = values[i];
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    if (typeof v === 'string') { const n = Number(v); if (Number.isFinite(n)) return n; }
    return fb;
}
function shiftMesh(m: MeshData, d: [number, number, number]): MeshData {
    return { vertices: m.vertices.map((v) => [v[0] + d[0], v[1] + d[1], (v[2] ?? 0) + d[2]]), indices: m.indices };
}
/** Watertight convex hull of a point cloud (three-stdlib ConvexGeometry). */
function convexHullMesh(pts: Pt3[]): MeshData | null {
    if (pts.length < 4) return null;
    try {
        const geo = new ConvexGeometry(pts.map((p) => new THREE.Vector3(p[0], p[1], p[2])));
        const pos = geo.getAttribute('position') as THREE.BufferAttribute;
        const vertices: number[][] = [];
        for (let i = 0; i < pos.count; i++) vertices.push([pos.getX(i), pos.getY(i), pos.getZ(i)]);
        const index = geo.getIndex();
        const indices: number[][] = [];
        if (index) for (let i = 0; i < index.count; i += 3) indices.push([index.getX(i), index.getX(i + 1), index.getX(i + 2)]);
        else for (let i = 0; i < pos.count; i += 3) indices.push([i, i + 1, i + 2]);
        geo.dispose();
        return indices.length > 0 ? { vertices, indices } : null;
    } catch {
        return null;
    }
}
function combineMeshes(list: MeshData[]): MeshData {
    const vertices: number[][] = [];
    const indices: number[][] = [];
    list.forEach((m) => {
        const off = vertices.length;
        vertices.push(...m.vertices);
        m.indices.forEach((t) => indices.push([t[0] + off, t[1] + off, t[2] + off]));
    });
    return { vertices, indices };
}

interface Plane { px: number; py: number; pz: number; nx: number; ny: number; nz: number }
function planeFromPoints(a: Pt3, b: Pt3, c: Pt3): Plane {
    const u = [b[0] - a[0], b[1] - a[1], b[2] - a[2]];
    const v = [c[0] - a[0], c[1] - a[1], c[2] - a[2]];
    const n = [u[1] * v[2] - u[2] * v[1], u[2] * v[0] - u[0] * v[2], u[0] * v[1] - u[1] * v[0]];
    const l = Math.hypot(...n) || 1;
    return { px: a[0], py: a[1], pz: a[2], nx: n[0] / l, ny: n[1] / l, nz: n[2] / l };
}
function side(p: number[], plane: Plane): number {
    return (p[0] - plane.px) * plane.nx + (p[1] - plane.py) * plane.ny + ((p[2] ?? 0) - plane.pz) * plane.nz;
}
/** Insert one point into the mesh (reusing a coincident vertex) and return
 *  its index. Keeps clipped halves sharing their cut-line vertices. */
function pushTri(target: MeshData, p: Pt3): number {
    const hit = target.vertices.findIndex((v) => Math.abs(v[0] - p[0]) < 1e-9 && Math.abs(v[1] - p[1]) < 1e-9 && Math.abs((v[2] ?? 0) - p[2]) < 1e-9);
    if (hit >= 0) return hit;
    target.vertices.push([p[0], p[1], p[2]]);
    return target.vertices.length - 1;
}
/** Clip one triangle by a plane; returns 0-2 triangles where side*signed > 0. */
function cutTriangleSide(pts: Pt3[], plane: Plane, sign: 1 | -1): Pt3[][] {
    const s = pts.map((p) => side(p, plane) * sign);
    const keep: Pt3[] = [];
    for (let i = 0; i < 3; i++) {
        const j = (i + 1) % 3;
        if (s[i] >= 0) keep.push(pts[i]);
        if ((s[i] >= 0) !== (s[j] >= 0)) {
            const t = s[i] / (s[i] - s[j] || 1e-9);
            keep.push([
                pts[i][0] + (pts[j][0] - pts[i][0]) * t,
                pts[i][1] + (pts[j][1] - pts[i][1]) * t,
                pts[i][2] + (pts[j][2] - pts[i][2]) * t,
            ]);
        }
    }
    if (keep.length < 3) return [];
    if (keep.length === 3) return [keep];
    return [[keep[0], keep[1], keep[2]], [keep[0], keep[2], keep[3]]];
}

/** Split a closed mesh by a plane into two real solids (each gets a flat
 *  cap polygon along the cut). Crossing triangles are split, not duplicated. */
function splitByPlane(m: MeshData, plane: Plane): { a: MeshData | null; b: MeshData | null } {
    const pos: MeshData = { vertices: [], indices: [] };
    const neg: MeshData = { vertices: [], indices: [] };
    const posCut: Pt3[] = [];
    const negCut: Pt3[] = [];
    // seed both halves with the non-crossing vertices only via pushTri
    m.indices.forEach((t) => {
        const pts = t.map((i) => m.vertices[i] as Pt3);
        const s = pts.map((p) => side(p, plane));
        if (s.every((x) => x >= 0)) { pos.indices.push(pts.map((p) => pushTri(pos, p))); return; }
        if (s.every((x) => x <= 0)) { neg.indices.push(pts.map((p) => pushTri(neg, p))); return; }
        cutTriangleSide(pts, plane, 1).forEach((tri) => {
            pos.indices.push(tri.map((p) => pushTri(pos, p)));
            posCut.push(...tri);
        });
        cutTriangleSide(pts, plane, -1).forEach((tri) => {
            neg.indices.push(tri.map((p) => pushTri(neg, p)));
            negCut.push(...tri);
        });
    });
    // flat caps: the new boundary vertices of each half form a planar ring;
    // ear-clip them in the plane's 2D basis and weld back (flipped winding).
    const capFor = (mesh: MeshData, ring: Pt3[], flip: boolean) => {
        if (ring.length < 3) return;
        const c = centroid(ring);
        const [t1, t2] = orthoBasis(plane);
        const pts2 = dedupePts(ring).map((p) => {
            const d: Pt3 = [p[0] - c[0], p[1] - c[1], p[2] - c[2]];
            return [dot(d, t1), dot(d, t2), 0] as Pt3;
        });
        if (pts2.length < 3) return;
        const tris = triangulatePolygon(pts2);
        tris.forEach((tri) => {
            const tri3 = tri.map((i) => addPts(c, mulPts(pts2[i], t1, t2)));
            const idx = tri3.map((p) => pushTri(mesh, p));
            mesh.indices.push(flip ? [idx[2], idx[1], idx[0]] : [idx[0], idx[1], idx[2]]);
        });
    };
    capFor(pos, posCut, false);
    capFor(neg, negCut, true);
    const ok = (x: MeshData) => x.indices.length > 0 ? x : null;
    return { a: ok(pos), b: ok(neg) };
}
function dedupePts(pts: Pt3[]): Pt3[] {
    const out: Pt3[] = [];
    pts.forEach((p) => { if (!out.some((q) => Math.hypot(q[0] - p[0], q[1] - p[1], q[2] - p[2]) < 1e-7)) out.push(p); });
    return out;
}
function orthoBasis(plane: Plane): [Pt3, Pt3] {
    const n: Pt3 = [plane.nx, plane.ny, plane.nz];
    const tmp: Pt3 = Math.abs(n[2]) < 0.9 ? [0, 0, 1] : [1, 0, 0];
    const t1 = norm3(cross(tmp, n));
    const t2 = cross(n, t1);
    return [t1, t2];
}
function cross(a: Pt3, b: Pt3): Pt3 { return [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]]; }
function dot(a: Pt3, b: Pt3): number { return a[0] * b[0] + a[1] * b[1] + a[2] * b[2]; }
function norm3(a: Pt3): Pt3 { const l = Math.hypot(a[0], a[1], a[2]) || 1; return [a[0] / l, a[1] / l, a[2] / l]; }
function mulPts(p: Pt3, t1: Pt3, t2: Pt3): Pt3 {
    return [p[0] * t1[0] + p[1] * t2[0], p[0] * t1[1] + p[1] * t2[1], p[0] * t1[2] + p[1] * t2[2]];
}
function addPts(a: Pt3, b: Pt3): Pt3 { return [a[0] + b[0], a[1] + b[1], a[2] + b[2]]; }
function mirror(p: Pt3, plane: Plane): Pt3 {
    const s = side(p, plane);
    return [p[0] - 2 * s * plane.nx, p[1] - 2 * s * plane.ny, (p[2] ?? 0) - 2 * s * plane.nz] as Pt3;
}
/** Ordered naked boundary loops of a triangle mesh (vertex index rings). */
function boundaryLoops(m: MeshData): number[][] {    const use = new Map<string, number>();
    const add = (a: number, b: number) => {
        const k = a < b ? `${a}:${b}` : `${b}:${a}`;
        use.set(k, (use.get(k) ?? 0) + 1);
    };
    m.indices.forEach(([a, b, c]) => { add(a, b); add(b, c); add(c, a); });
    const adj = new Map<number, number[]>();
    use.forEach((count, key) => {
        if (count !== 1) return;
        const [a, b] = key.split(':').map(Number);
        adj.set(a, [...(adj.get(a) ?? []), b]);
        adj.set(b, [...(adj.get(b) ?? []), a]);
    });
    const loops: number[][] = [];
    const used = new Set<number>();
    adj.forEach((_neighbors, start) => {
        if (used.has(start)) return;
        const loop: number[] = [start];
        used.add(start);
        let cur = start, prev = -1;
        for (let guard = 0; guard < 10000; guard++) {
            const next = (adj.get(cur) ?? []).find((n) => n !== prev && !used.has(n));
            if (next === undefined) break;
            if (next === start) break;
            loop.push(next); used.add(next); prev = cur; cur = next;
        }
        if (loop.length >= 3) loops.push(loop);
    });
    return loops;
}
/**
 * Top-view hidden-line removal: returns the VISIBLE edges of the given
 * meshes (each as a 2-point XY polyline at Z=0). An edge is hidden when
 * some other face projects over its midpoint from above. Self faces that
 * share the edge are skipped (they don't occlude their own boundary).
 */
function hiddenLineEdges(meshes: { verts: Pt3[]; faces: number[][] }[]): Pt3[][] {
    const allFaces: { tri: [Pt3, Pt3, Pt3]; mesh: number }[] = [];
    meshes.forEach((m, mi) => m.faces.forEach((f) => {
        if (f.length < 3) return;
        allFaces.push({ tri: [m.verts[f[0]], m.verts[f[1]], m.verts[f[2]]], mesh: mi });
    }));
    const edgeSeen = new Set<string>();
    const out: Pt3[][] = [];
    meshes.forEach((m, mi) => {
        m.faces.forEach((f) => {
            if (f.length < 3) return;
            for (let e = 0; e < 3; e++) {
                const a = m.verts[f[e]], b = m.verts[f[(e + 1) % 3]];
                const key = a[0] < b[0] || (a[0] === b[0] && a[1] < b[1]) ? `${a[0]},${a[1]}|${b[0]},${b[1]}` : `${b[0]},${b[1]}|${a[0]},${a[1]}`;
                if (edgeSeen.has(key)) continue;
                edgeSeen.add(key);
                const mid: Pt3 = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2, (a[2] + b[2]) / 2];
                let visible = true;
                for (const face of allFaces) {
                    if (faceContainsEdge(face.tri, a, b)) continue;
                    if (pointAboveFace(face.tri, mid)) { visible = false; break; }
                }
                if (visible) out.push([[a[0], a[1], 0], [b[0], b[1], 0]]);
                void mi;
            }
        });
    });
    return out;
}
function faceContainsEdge(tri: [Pt3, Pt3, Pt3], a: Pt3, b: Pt3): boolean {
    const has = (p: Pt3) => tri.some((t) => Math.hypot(t[0] - p[0], t[1] - p[1], t[2] - p[2]) < 1e-6);
    return has(a) && has(b);
}
/** XY point-in-triangle + "strictly above" test for a top-view occluder. */
function pointAboveFace(tri: [Pt3, Pt3, Pt3], p: Pt3): boolean {
    const [A, B, C] = tri;
    const z = (A[2] + B[2] + C[2]) / 3;
    if (z <= p[2] + 1e-4) return false;
    const d = (px: number, py: number, x1: number, y1: number, x2: number, y2: number) => (px - x1) * (y2 - y1) - (py - y1) * (x2 - x1);
    const d1 = d(p[0], p[1], A[0], A[1], B[0], B[1]);
    const d2 = d(p[0], p[1], B[0], B[1], C[0], C[1]);
    const d3 = d(p[0], p[1], C[0], C[1], A[0], A[1]);
    const neg = d1 < 0 || d2 < 0 || d3 < 0;
    const pos = d1 > 0 || d2 > 0 || d3 > 0;
    return !(neg && pos);
}
/**
 * Plane/mesh section: collect the crossing segment of each face (2 points
 * on mesh edges), then chain segments through shared mesh edges into closed
 * loops — correct for non-star-shaped sections (angle sort is the fallback
 * only when the chain leaves an open fragment).
 */
function sectionLoop(m: MeshData, plane: Plane): Pt3[] | null {
    type Seg = { a: number; b: number; pa: Pt3; pb: Pt3 };
    const segs: Seg[] = [];
    // per-face crossing: exactly two sign changes → segment on edge pair
    m.indices.forEach(([a, b, c]) => {
        const ids = [a, b, c];
        const s = ids.map((i) => side(m.vertices[i], plane));
        const crossings: { edgeId: string; otherId: string; pt: Pt3 }[] = [];
        for (let i = 0; i < 3; i++) {
            const j = (i + 1) % 3;
            if ((s[i] < 0) !== (s[j] < 0)) {
                const pa = m.vertices[ids[i]], pb = m.vertices[ids[j]];
                const t = s[i] / (s[i] - s[j] || 1e-9);
                crossings.push({
                    edgeId: `${Math.min(ids[i], ids[j])}:${Math.max(ids[i], ids[j])}`,
                    otherId: `${Math.min(ids[i], ids[j])}`,
                    pt: [pa[0] + (pb[0] - pa[0]) * t, pa[1] + (pb[1] - pa[1]) * t, pa[2] + (pb[2] - pa[2]) * t] as Pt3,
                });
            }
        }
        if (crossings.length === 2) {
            segs.push({ a: -1, b: -1, pa: crossings[0].pt, pb: crossings[1].pt });
            (segs[segs.length - 1] as Seg & { ea: string; eb: string }).ea = crossings[0].edgeId;
            (segs[segs.length - 1] as Seg & { ea: string; eb: string }).eb = crossings[1].edgeId;
        }
    });
    if (segs.length < 3) return null;
    const edgeOf = (s: Seg) => s as Seg & { ea: string; eb: string };
    // chain: endpoint of one segment connects to the start of the next via
    // the same mesh edge (the two segments lie on the same mesh edge).
    const adj = new Map<string, { si: number; end: 'a' | 'b' }[]>();
    segs.forEach((s, si) => {
        const { ea, eb } = edgeOf(s);
        adj.set(ea, [...(adj.get(ea) ?? []), { si, end: 'a' }]);
        adj.set(eb, [...(adj.get(eb) ?? []), { si, end: 'b' }]);
    });
    const used = new Set<number>();
    let best: Pt3[] | null = null;
    for (let start = 0; start < segs.length; start++) {
        if (used.has(start)) continue;
        const loop: Pt3[] = [segs[start].pa, segs[start].pb];
        used.add(start);
        let prevEnd: 'a' | 'b' = 'b';
        let prevSeg = edgeOf(segs[start]);
        let curEdge = prevEnd === 'b' ? prevSeg.eb : prevSeg.ea;
        for (let guard = 0; guard < segs.length + 2; guard++) {
            const next = (adj.get(curEdge) ?? []).find((x) => !used.has(x.si));
            if (!next) break;
            used.add(next.si);
            const ns = edgeOf(segs[next.si]);
            const entryEnd = next.end;
            const exitEnd: 'a' | 'b' = entryEnd === 'a' ? 'b' : 'a';
            loop.push(entryEnd === 'a' ? ns.pb : ns.pa);
            curEdge = exitEnd === 'b' ? ns.eb : ns.ea;
            if (curEdge === edgeOf(segs[start]).ea) break; // closed
            prevEnd = exitEnd;
        }
        if (!best || loop.length > best.length) best = loop;
    }
    if (!best || best.length < 3) {
        // open-fragment fallback: angle sort (documented limitation)
        const pts = segs.map((s) => s.pa).concat(segs.map((s) => s.pb));
        const c = centroid(pts);
        pts.sort((p, q) => Math.atan2(p[1] - c[1], p[0] - c[0]) - Math.atan2(q[1] - c[1], q[0] - c[0]));
        return [...pts, pts[0]];
    }
    return [...best, best[0]];
}
